import { useNotification } from '@/hooks/use-notification';
import { runSimulationFilesImport } from '@/pages/price-planning/simulation/utils/import-simulation-files-runner';
import { formatDateAsDdMmYyyyPrefix } from '@/pages/price-planning/simulation/utils/upload-simulation-helpers';
import { formatDateStringIso, parseDateString } from '@/utils/date-utils';
import {
  Button,
  DatePicker,
  Label,
  Modal,
} from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import { useQueryClient } from '@tanstack/react-query';
import { type RefObject, useState } from 'react';

type ImportSimulationFilesModalProps = {
  isOpen: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  onClose: () => void;
};

const ImportSimulationFilesModal = ({
  isOpen,
  triggerRef,
  onClose,
}: ImportSimulationFilesModalProps) => {
  const queryClient = useQueryClient();
  const showNotification = useNotification();
  const [modalNode, setModalNode] = useState<HTMLDivElement | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const reset = () => {
    setSelectedDate(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleStartImport = () => {
    if (!selectedDate) return;

    const prefix = formatDateAsDdMmYyyyPrefix(selectedDate);

    // Fire and forget: the import runs to completion in the background, independent of this
    // modal, and reports its result via a single toast once every file has been processed.
    runSimulationFilesImport({ prefix, queryClient, showNotification });

    showNotification({
      type: 'info',
      title: 'Import started',
      description: `Processing simulation files for ${prefix} in the background. You'll be notified when it's done.`,
    });

    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="medium"
      triggerRef={triggerRef}
      ref={setModalNode}
    >
      <Modal.Title>Import &amp; Compare Simulation Files</Modal.Title>
      <Modal.Content style={{ padding: '24px' }}>
        <Flex flexDirection="column" gap="16px">
          <Text className="body-sm-regular">
            Select the date to import simulation workbooks for. Every file found
            for that date will be extracted, compared against a draft
            simulation, and any mismatched fields will be recorded. You can
            close this window immediately — you'll get a notification once
            processing is complete.
          </Text>
          <div>
            <Label>Date</Label>
            <DatePicker
              mode="single"
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              portalTarget={modalNode}
              value={selectedDate ? formatDateStringIso(selectedDate) : null}
              onChange={(_, value) => {
                setSelectedDate(value ? parseDateString(value) : null);
              }}
            />
          </div>
        </Flex>
      </Modal.Content>
      <Modal.Footer>
        <Button
          onClick={handleClose}
          appearance="ghost"
          variant="neutral"
          aria-label="Cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={handleStartImport}
          appearance="solid"
          variant="primary"
          aria-label="Start import"
          disabled={!selectedDate}
        >
          Start import
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImportSimulationFilesModal;
